import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiMapPin, FiUser } from 'react-icons/fi';
import DateTimePicker from '../components/common/DateTimePicker';
import axios from 'axios';
import SEO from '../components/common/SEO';
import { useHydrated } from '../hooks/useHydrated';
import { kundliAPI } from '../services/api';
import './Kundli.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// South Indian Chart layout: fixed rashi positions
const SI_POSITIONS = [
  { row: 0, col: 1 }, // 0  மேஷம் (Aries)
  { row: 0, col: 2 }, // 1  ரிஷபம் (Taurus)
  { row: 0, col: 3 }, // 2  மிதுனம் (Gemini)
  { row: 1, col: 3 }, // 3  கடகம் (Cancer)
  { row: 2, col: 3 }, // 4  சிம்மம் (Leo)
  { row: 3, col: 3 }, // 5  கன்னி (Virgo)
  { row: 3, col: 2 }, // 6  துலாம் (Libra)
  { row: 3, col: 1 }, // 7  விருச்சிகம் (Scorpio)
  { row: 3, col: 0 }, // 8  தனுசு (Sagittarius)
  { row: 2, col: 0 }, // 9  மகரம் (Capricorn)
  { row: 1, col: 0 }, // 10 கும்பம் (Aquarius)
  { row: 0, col: 0 }, // 11 மீனம் (Pisces)
];

const RASHI_LABELS = [
  'மேஷ', 'ரிஷப', 'மிதுன', 'கடக', 'சிம்ம', 'கன்னி',
  'துலா', 'விருச்', 'தனுசு', 'மகர', 'கும்ப', 'மீன'
];

const PLANET_ABBREVS = {
  'ASC': 'லக்', 'Sun': 'சூ', 'Moon': 'சந்', 'Mars': 'செவ்', 'Mercury': 'புத',
  'Jupiter': 'குரு', 'Venus': 'சுக்', 'Saturn': 'சனி', 'Rahu': 'ரா', 'Ketu': 'கே'
};

const PLANET_TAMIL = {
  'ASC': 'லக்னம்', 'Sun': 'சூரியன்', 'Moon': 'சந்திரன்', 'Mars': 'செவ்வாய்',
  'Mercury': 'புதன்', 'Jupiter': 'குரு', 'Venus': 'சுக்கிரன்', 'Saturn': 'சனி',
  'Rahu': 'ராகு', 'Ketu': 'கேது'
};

// Rashi English-transliteration to Tamil
const RASHI_TAMIL = {
  'Mesham': 'மேஷம்', 'Rishabam': 'ரிஷபம்', 'Mithunam': 'மிதுனம்',
  'Katagam': 'கடகம்', 'Simham': 'சிம்மம்', 'Kanni': 'கன்னி',
  'Thulam': 'துலாம்', 'Vrischigam': 'விருச்சிகம்', 'Dhanusu': 'தனுசு',
  'Magaram': 'மகரம்', 'Kumbham': 'கும்பம்', 'Meenam': 'மீனம்',
  // English names fallback
  'Aries': 'மேஷம்', 'Taurus': 'ரிஷபம்', 'Gemini': 'மிதுனம்',
  'Cancer': 'கடகம்', 'Leo': 'சிம்மம்', 'Virgo': 'கன்னி',
  'Libra': 'துலாம்', 'Scorpio': 'விருச்சிகம்', 'Sagittarius': 'தனுசு',
  'Capricorn': 'மகரம்', 'Aquarius': 'கும்பம்', 'Pisces': 'மீனம்',
};

// Nakshatra to Tamil
const NAKSHATRA_TAMIL = {
  'Aswini': 'அசுவினி', 'Bharani': 'பரணி', 'Karthigai': 'கார்த்திகை',
  'Rohini': 'ரோகிணி', 'Mrigasheersham': 'மிருகசீரிஷம்', 'Thiruvathirai': 'திருவாதிரை',
  'Punarpoosam': 'புனர்பூசம்', 'Poosam': 'பூசம்', 'Aayilyam': 'ஆயில்யம்',
  'Makam': 'மகம்', 'Pooram': 'பூரம்', 'Uthiram': 'உத்திரம்',
  'Hastham': 'அஸ்தம்', 'Chithirai': 'சித்திரை', 'Swaathi': 'சுவாதி',
  'Vishakham': 'விசாகம்', 'Anusham': 'அனுஷம்', 'Kettai': 'கேட்டை',
  'Moolam': 'மூலம்', 'Pooradam': 'பூராடம்', 'Uthiraadam': 'உத்திராடம்',
  'Thiruvonam': 'திருவோணம்', 'Avittam': 'அவிட்டம்', 'Sathayam': 'சதயம்',
  'Pooratathi': 'பூரட்டாதி', 'Uthirattathi': 'உத்திரட்டாதி', 'Revathi': 'ரேவதி',
};

// Tithi to Tamil
const TITHI_TAMIL = {
  'Prathimai': 'பிரதமை', 'Dwithiai': 'துவிதியை', 'Thrithiai': 'திருதியை',
  'Chathurthi': 'சதுர்த்தி', 'Panchami': 'பஞ்சமி', 'Sashti': 'சஷ்டி',
  'Sapthami': 'சப்தமி', 'Ashtami': 'அஷ்டமி', 'Navami': 'நவமி',
  'Dasami': 'தசமி', 'Ekadasi': 'ஏகாதசி', 'Dwadasi': 'துவாதசி',
  'Thrayodasi': 'திரயோதசி', 'Chathurdasi': 'சதுர்த்தசி',
  'Pournami': 'பௌர்ணமி', 'Amavasai': 'அமாவாசை',
  // With prefix
  'Shukla Prathimai': 'சுக்ல பிரதமை', 'Shukla Dwithiai': 'சுக்ல துவிதியை',
  'Shukla Thrithiai': 'சுக்ல திருதியை', 'Shukla Chathurthi': 'சுக்ல சதுர்த்தி',
  'Shukla Panchami': 'சுக்ல பஞ்சமி', 'Shukla Sashti': 'சுக்ல சஷ்டி',
  'Shukla Sapthami': 'சுக்ல சப்தமி', 'Shukla Ashtami': 'சுக்ல அஷ்டமி',
  'Shukla Navami': 'சுக்ல நவமி', 'Shukla Dasami': 'சுக்ல தசமி',
  'Shukla Ekadasi': 'சுக்ல ஏகாதசி', 'Shukla Dwadasi': 'சுக்ல துவாதசி',
  'Shukla Thrayodasi': 'சுக்ல திரயோதசி', 'Shukla Chathurdasi': 'சுக்ல சதுர்த்தசி',
  'Krishna Prathimai': 'கிருஷ்ண பிரதமை', 'Krishna Dwithiai': 'கிருஷ்ண துவிதியை',
  'Krishna Thrithiai': 'கிருஷ்ண திருதியை', 'Krishna Chathurthi': 'கிருஷ்ண சதுர்த்தி',
  'Krishna Panchami': 'கிருஷ்ண பஞ்சமி', 'Krishna Sashti': 'கிருஷ்ண சஷ்டி',
  'Krishna Sapthami': 'கிருஷ்ண சப்தமி', 'Krishna Ashtami': 'கிருஷ்ண அஷ்டமி',
  'Krishna Navami': 'கிருஷ்ண நவமி', 'Krishna Dasami': 'கிருஷ்ண தசமி',
  'Krishna Ekadasi': 'கிருஷ்ண ஏகாதசி', 'Krishna Dwadasi': 'கிருஷ்ண துவாதசி',
  'Krishna Thrayodasi': 'கிருஷ்ண திரயோதசி', 'Krishna Chathurdasi': 'கிருஷ்ண சதுர்த்தசி',
};

// Paksha to Tamil
const PAKSHA_TAMIL = {
  'Shukla Paksham': 'சுக்ல பக்ஷம்', 'Krishna Paksham': 'கிருஷ்ண பக்ஷம்',
  'Shukla': 'சுக்ல பக்ஷம்', 'Krishna': 'கிருஷ்ண பக்ஷம்',
};

// Helper to translate any value through maps
const toTamil = (value, ...maps) => {
  if (!value) return '-';
  for (const map of maps) {
    if (map[value]) return map[value];
  }
  return value;
};

// Translate nakshatra with padam (e.g., "Makam - 1 Padam" → "மகம் - 1 பாதம்")
const translateNakshatra = (value) => {
  if (!value) return '-';
  const match = value.match(/^(.+?)\s*-\s*(\d+)\s*Padam$/i);
  if (match) {
    const nakshatraName = match[1].trim();
    const padam = match[2];
    return `${toTamil(nakshatraName, NAKSHATRA_TAMIL)} - ${padam} பாதம்`;
  }
  return toTamil(value, NAKSHATRA_TAMIL);
};

// Translate balance dasha (e.g., "Ketu 6Y 2M 12D" → "கேது 6வ 2மா 12நா")
const translateBalanceDasha = (value) => {
  if (!value) return '-';
  const match = value.match(/^(\w+)\s+(\d+)Y\s+(\d+)M\s+(\d+)D$/i);
  if (match) {
    const planet = match[1];
    return `${toTamil(planet, PLANET_TAMIL)} ${match[2]}வ ${match[3]}மா ${match[4]}நா`;
  }
  return value;
};

/** South Indian Chart SVG Component - Tamil */
const SouthIndianChart = ({ rasiChart, lagnaRashi, title }) => {
  const cellSize = 90;
  const width = cellSize * 4;
  const height = cellSize * 4;

  const planetsByRashi = {};
  if (rasiChart) {
    for (let house = 1; house <= 12; house++) {
      const rashiIndex = (lagnaRashi + house - 1) % 12;
      const planetsInHouse = rasiChart[house] || [];
      if (planetsInHouse.length > 0) {
        planetsByRashi[rashiIndex] = planetsInHouse.map(p =>
          PLANET_ABBREVS[p.replace('(R)', '')] + (p.includes('(R)') ? 'ᴿ' : '')
        );
      }
    }
  }

  return (
    <div className="si-chart-container">
      {title && <h4 className="chart-title">{title}</h4>}
      <svg viewBox={`0 0 ${width} ${height}`} className="si-chart-svg">
        <rect x="0" y="0" width={width} height={height} fill="#fffaf5" stroke="#ff6f00" strokeWidth="2" rx="4" />
        {SI_POSITIONS.map((pos, rashiIdx) => {
          const x = pos.col * cellSize;
          const y = pos.row * cellSize;
          const isLagna = rashiIdx === lagnaRashi;
          const planets = planetsByRashi[rashiIdx] || [];

          return (
            <g key={rashiIdx}>
              <rect
                x={x} y={y} width={cellSize} height={cellSize}
                fill={isLagna ? 'rgba(255,111,0,0.08)' : 'transparent'}
                stroke="#ddd" strokeWidth="1"
              />
              <text x={x + 5} y={y + 14} fontSize="10" fill="#999" fontFamily="Noto Sans Tamil, Poppins">
                {RASHI_LABELS[rashiIdx]}
              </text>
              {isLagna && (
                <text x={x + cellSize - 5} y={y + 14} fontSize="10" fill="#ff6f00" fontFamily="Noto Sans Tamil, Poppins" textAnchor="end" fontWeight="bold">
                  லக்
                </text>
              )}
              {planets.map((p, i) => (
                <text
                  key={i}
                  x={x + cellSize / 2}
                  y={y + 32 + i * 16}
                  fontSize="12"
                  fill={p.includes('ᴿ') ? '#c62828' : '#1a1a1a'}
                  fontFamily="Noto Sans Tamil, Poppins"
                  fontWeight="600"
                  textAnchor="middle"
                >
                  {p}
                </text>
              ))}
            </g>
          );
        })}
        <rect x={cellSize} y={cellSize} width={cellSize * 2} height={cellSize * 2} fill="#fffaf5" stroke="#ff6f00" strokeWidth="1.5" rx="2" />
        <text x={width / 2} y={height / 2 - 5} fontSize="14" fill="#ff6f00" fontFamily="Noto Sans Tamil, Cinzel" fontWeight="700" textAnchor="middle">
          {title || 'ராசி கட்டம்'}
        </text>
        <text x={width / 2} y={height / 2 + 12} fontSize="10" fill="#999" fontFamily="Noto Sans Tamil, Poppins" textAnchor="middle">
          தென்னிந்திய முறை
        </text>
      </svg>
    </div>
  );
};

/** Planet positions table - Tamil */
const PlanetTable = ({ planets }) => {
  if (!planets || planets.length === 0) return null;
  return (
    <div className="planets-table">
      <h4>கிரக நிலைகள்</h4>
      <table>
        <thead>
          <tr>
            <th>கிரகம்</th>
            <th>நிலை (டிகிரி)</th>
            <th>ராசி</th>
            <th>நட்சத்திரம்</th>
            <th>பாதம்</th>
            <th>அதிபதி</th>
          </tr>
        </thead>
        <tbody>
          {planets.map((p, i) => (
            <tr key={i} className={p.isRetrograde ? 'retro-row' : ''}>
              <td>
                <span className="planet-symbol">{p.symbol}</span>
                {PLANET_TAMIL[p.name] || p.name}{p.isRetrograde ? ' (வ)' : ''}
              </td>
              <td>{p.positionDegree}</td>
              <td>{toTamil(p.rashi, RASHI_TAMIL)}</td>
              <td>{toTamil(p.nakshatra, NAKSHATRA_TAMIL)}</td>
              <td>{p.pada}</td>
              <td>{toTamil(p.nakshatraLord, PLANET_TAMIL)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/** Dasha-Bhukti 120 Year Analysis - Tamil */
const DashaSection = ({ dasha }) => {
  const [expandedDasha, setExpandedDasha] = useState(null);

  if (!dasha) return null;

  const now = new Date();

  const currentMahaIndex = dasha.allMahaDashas
    ? dasha.allMahaDashas.findIndex(md => {
        const s = new Date(md.startDate);
        const e = new Date(md.endDate);
        return now >= s && now < e;
      })
    : -1;

  const toggleExpand = (index) => {
    setExpandedDasha(expandedDasha === index ? null : index);
  };

  const activeExpanded = expandedDasha !== null ? expandedDasha : currentMahaIndex;

  return (
    <div className="dasha-section">
      <h4>விம்சோத்தரி திசை - 120 வருட பலன்கள்</h4>

      {dasha.currentMahaDasha && (
        <div className="current-dasha-card">
          <div className="current-label">தற்போதைய திசை</div>
          <div className="current-info">
            <span className="dasha-lord">{toTamil(dasha.currentMahaDasha.lord, PLANET_TAMIL)} மகா திசை</span>
            {dasha.currentAntarDasha && (
              <span className="antar-lord"> &rarr; {toTamil(dasha.currentAntarDasha.lord, PLANET_TAMIL)} புக்தி</span>
            )}
          </div>
          <div className="dasha-dates">
            {dasha.currentMahaDasha.start} முதல் {dasha.currentMahaDasha.end} வரை
          </div>
        </div>
      )}

      <div className="dasha-full-table">
        {dasha.allMahaDashas && dasha.allMahaDashas.map((md, i) => {
          const start = new Date(md.startDate);
          const end = new Date(md.endDate);
          const isCurrent = now >= start && now < end;
          const isPast = now >= end;
          const isExpanded = activeExpanded === i;

          return (
            <div key={i} className={`dasha-block ${isCurrent ? 'current' : ''} ${isPast ? 'past' : ''}`}>
              <div className="dasha-block-header" onClick={() => toggleExpand(i)}>
                <div className="dasha-block-left">
                  <span className={`dasha-expand-icon ${isExpanded ? 'expanded' : ''}`}>&#9654;</span>
                  <span className="dasha-block-lord">{toTamil(md.lord, PLANET_TAMIL)}</span>
                  <span className="dasha-block-years">{md.durationYears} வ</span>
                </div>
                <div className="dasha-block-right">
                  <span className="dasha-block-dates">{md.startDate} - {md.endDate}</span>
                  {isCurrent && <span className="dasha-current-badge">நடப்பு</span>}
                </div>
              </div>

              {isExpanded && md.antarDashas && (
                <div className="bhukti-table">
                  <table>
                    <thead>
                      <tr>
                        <th>புக்தி அதிபதி</th>
                        <th>தொடக்கம்</th>
                        <th>முடிவு</th>
                        <th>காலம்</th>
                      </tr>
                    </thead>
                    <tbody>
                      {md.antarDashas.map((ad, j) => {
                        const adStart = new Date(ad.startDate);
                        const adEnd = new Date(ad.endDate);
                        const isAdCurrent = now >= adStart && now < adEnd;
                        const isAdPast = now >= adEnd;

                        const years = Math.floor(ad.durationYears);
                        const remMonths = (ad.durationYears - years) * 12;
                        const months = Math.floor(remMonths);
                        const days = Math.round((remMonths - months) * 30);
                        const durationStr = `${years}வ ${months}மா ${days}நா`;

                        return (
                          <tr key={j} className={`${isAdCurrent ? 'bhukti-current' : ''} ${isAdPast ? 'bhukti-past' : ''}`}>
                            <td className="bhukti-lord">
                              {toTamil(md.lord, PLANET_TAMIL)}-{toTamil(ad.lord, PLANET_TAMIL)}
                              {isAdCurrent && <span className="bhukti-running">நடப்பு</span>}
                            </td>
                            <td>{ad.startDate}</td>
                            <td>{ad.endDate}</td>
                            <td>{durationStr}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Basic horoscope details summary - Tamil */
const BasicDetails = ({ horoscope }) => {
  if (!horoscope) return null;

  const details = [
    { label: 'அட்சரேகை', value: horoscope.birthDetails.latitude },
    { label: 'தீர்க்கரேகை', value: horoscope.birthDetails.longitude },
    { label: 'லக்னம்', value: toTamil(horoscope.lagna.rashi, RASHI_TAMIL) },
    { label: 'லக்ன அதிபதி', value: toTamil(horoscope.lagna.lord, PLANET_TAMIL) },
    { label: 'ஜென்ம ராசி', value: toTamil(horoscope.birthSign, RASHI_TAMIL), highlight: true, highlightLabel: true },
    { label: 'நட்சத்திரம் (பாதம்)', value: translateNakshatra(horoscope.nakshatra), highlight: true, highlightLabel: true },
    { label: 'திதி', value: toTamil(horoscope.tithi?.name, TITHI_TAMIL) },
    { label: 'பக்ஷம்', value: toTamil(horoscope.tithi?.paksha, PAKSHA_TAMIL) },
    { label: 'அயனாம்சம்', value: `லஹிரி - ${horoscope.ayanamsa}\u00B0` },
    { label: 'திசை இருப்பு', value: translateBalanceDasha(horoscope.balanceDashaFormatted) },
  ];

  return (
    <div className="basic-details">
      <h4>அடிப்படை விவரங்கள்</h4>
      <div className="details-grid">
        {details.map((d, i) => (
          <div key={i} className="detail-row">
            <span className={`detail-label${d.highlightLabel ? ' highlight' : ''}`}>{d.label}</span>
            <span className={`detail-value${d.highlight ? ' highlight' : ''}`}>{d.value || '-'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/** Shadbala Strength bars - Tamil */
const ShadbalaSection = ({ shadbala, manglik }) => {
  if (!shadbala || shadbala.length === 0) return null;

  const maxTotal = Math.max(...shadbala.map(s => s.total));

  return (
    <div className="shadbala-section">
      <h4>ஷட்பல (கிரக வலிமை)</h4>
      <div className="strength-bars">
        {shadbala.map((s, i) => (
          <div key={i} className="strength-row">
            <span className="strength-planet">{PLANET_TAMIL[s.planet] || s.planet}</span>
            <div className="strength-bar-bg">
              <div
                className="strength-bar-fill"
                style={{ width: `${(s.total / maxTotal) * 100}%` }}
              ></div>
            </div>
            <span className="strength-value">{s.total.toFixed(2)}</span>
          </div>
        ))}
      </div>

      {manglik && (
        <div className={`manglik-badge ${manglik.isManglik ? 'is-manglik' : 'no-manglik'}`}>
          <strong>செவ்வாய் தோஷம்:</strong> {manglik.isManglik ? `ஆம் - ${manglik.severity}` : 'இல்லை'}
          {manglik.exceptions && manglik.exceptions.length > 0 && (
            <span className="manglik-exceptions"> ({manglik.exceptions.join(', ')})</span>
          )}
        </div>
      )}
    </div>
  );
};

const JathagamInTamil = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    gender: '',
    date: '',
    time: '',
    place: ''
  });
  const [horoscope, setHoroscope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const suggestionsRef = useRef(null);
  const resultRef = useRef(null);
  const hydrated = useHydrated();

  useEffect(() => {
    if (horoscope && resultRef.current && window.innerWidth <= 900) {
      setTimeout(() => {
        resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [horoscope]);

  useEffect(() => {
    if (location.state?.formData) {
      const navFormData = location.state.formData;
      const navLocation = location.state.selectedLocation;
      setFormData(navFormData);

      window.history.replaceState({}, document.title);

      if (navFormData.date && navFormData.time) {
        (async () => {
          try {
            setLoading(true);
            setError('');

            let city = navLocation;

            if (!city && navFormData.place) {
              const cityRes = await axios.get(`${API_BASE_URL}/panchang/cities`, {
                params: { q: navFormData.place }
              });
              const cities = cityRes.data.data || [];
              if (cities.length > 0) {
                city = cities[0];
              }
            }

            if (city) {
              setSelectedLocation(city);
              const displayName = city.state
                ? `${city.city}, ${city.state}, ${city.country}`
                : `${city.city}, ${city.country}`;
              setFormData(prev => ({ ...prev, place: displayName }));

              const payload = {
                name: navFormData.name,
                gender: navFormData.gender,
                date: navFormData.date,
                time: navFormData.time,
                place: city.city,
                lat: city.latitude,
                lon: city.longitude,
                tz: city.timezone,
                state: city.state,
                country: city.country,
              };

              const response = await kundliAPI.generateKundli(payload);
              if (response.data.success) {
                setHoroscope(response.data.data);

              } else {
                setError(response.data.error || 'ஜாதகம் உருவாக்க இயலவில்லை');
              }
            } else {
              setError('நகரம் கிடைக்கவில்லை. தயவுசெய்து நகரத்தைத் தேடி தேர்வு செய்யவும்.');
            }
          } catch (err) {
            setError(err.response?.data?.error || err.message || 'ஜாதகம் உருவாக்க இயலவில்லை');
          } finally {
            setLoading(false);
          }
        })();
      }
    }
  }, [location.state]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchCities = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const response = await axios.get(`${API_BASE_URL}/panchang/cities`, {
        params: { q: query }
      });
      setSuggestions(response.data.data || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.place.length >= 2 && !selectedLocation) {
        searchCities(formData.place);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [formData.place, searchCities, selectedLocation]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (name === 'place') {
      setSelectedLocation(null);
    }
  };

  const handleCitySelect = (city) => {
    const displayName = city.state
      ? `${city.city}, ${city.state}, ${city.country}`
      : `${city.city}, ${city.country}`;
    setFormData(prev => ({ ...prev, place: displayName }));
    setSelectedLocation(city);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setHoroscope(null);

    try {
      const payload = {
        name: formData.name,
        gender: formData.gender,
        date: formData.date,
        time: formData.time,
        place: formData.place,
      };

      if (selectedLocation) {
        payload.lat = selectedLocation.latitude;
        payload.lon = selectedLocation.longitude;
        payload.tz = selectedLocation.timezone;
        payload.place = selectedLocation.city;
        payload.state = selectedLocation.state;
        payload.country = selectedLocation.country;
      }

      const response = await kundliAPI.generateKundli(payload);
      if (response.data.success) {
        setHoroscope(response.data.data);
      } else {
        setError(response.data.error || 'ஜாதகம் உருவாக்க இயலவில்லை');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'ஜாதகம் உருவாக்க இயலவில்லை');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="kundli-page">
      <SEO
        title="ஜாதகம் தமிழில் - இலவச ஜாதக கட்டம் உருவாக்கி"
        description="இலவசமாக உங்கள் ஜாதகத்தை தமிழில் உருவாக்குங்கள். ராசி கட்டம், நவாம்ச கட்டம், கிரக நிலைகள், 120 வருட விம்சோத்தரி திசை பலன்கள், ஷட்பல வலிமை மற்றும் செவ்வாய் தோஷ பரிசோதனை ஆகியவற்றை பெறுங்கள்."
        keywords="ஜாதகம் தமிழில், jathagam in tamil, tamil horoscope, tamil jathagam, ராசி கட்டம், நவாம்சம், திசை புக்தி, free tamil horoscope generator, இலவச ஜாதகம், கிரக நிலை"
        canonicalPath="/jathagam-in-tamil"
        locale="ta_IN"
        hreflang={[
          { lang: 'en', path: '/online-horoscope-generator' },
          { lang: 'ta', path: '/jathagam-in-tamil' },
        ]}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "ஜாதகம் தமிழில் - இலவச ஜாதக கட்டம் உருவாக்கி",
          "applicationCategory": "LifestyleApplication",
          "operatingSystem": "Web Browser",
          "inLanguage": "ta",
          "description": "இலவசமாக உங்கள் ஜாதகத்தை தமிழில் உருவாக்குங்கள் - ராசி கட்டம், நவாம்சம், திசை புக்தி, கிரக வலிமை",
          "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" }
        }, {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {"@type": "Question", "name": "இலவச ஜாதக உருவாக்கி துல்லியமானதா?", "acceptedAnswer": {"@type": "Answer", "text": "ஆம், AstroEpanchang சுவிஸ் எபிமெரிஸ் கணக்கீடுகளை லஹிரி அயனாம்சத்துடன் பயன்படுத்துகிறது — இது இந்தியாவில் தொழில்முறை ஜோதிடர்கள் பயன்படுத்தும் அதே தரநிலையாகும். துல்லியம் சரியான பிறப்பு நேரத்தை உள்ளிடுவதில் தங்கியுள்ளது, ஏனெனில் சில நிமிட வேறுபாடு கூட லக்னம் மற்றும் திசை காலத்தை மாற்றக்கூடும்."}},
            {"@type": "Question", "name": "ஜாதகம் உருவாக்க என்ன தகவல்கள் தேவை?", "acceptedAnswer": {"@type": "Answer", "text": "உங்கள் சரியான பிறந்த தேதி, பிறந்த நேரம் (முடிந்தவரை துல்லியமாக — மருத்துவமனை பதிவுகள் சிறந்தவை), மற்றும் பிறந்த இடம் (நகரம்/ஊர்) தேவை. பிறந்த இடம் புவியியல் ஆயத்தொலைவுகள் மற்றும் நேர வலயத்தை தீர்மானிக்க பயன்படுத்தப்படுகிறது."}},
            {"@type": "Question", "name": "ராசி கட்டம் மற்றும் நவாம்ச கட்டம் என்ன வேறுபாடு?", "acceptedAnswer": {"@type": "Answer", "text": "ராசி கட்டம் (D-1) பிறப்பின் போது கிரக நிலைகளைக் காட்டும் முதன்மை ஜாதக கட்டமாகும். நவாம்ச கட்டம் (D-9) ஒவ்வொரு ராசியையும் 9 சம பாகங்களாக பிரிக்கிறது மற்றும் திருமண பொருத்தம் மற்றும் ஆன்மீக பகுப்பாய்வுக்கு பயன்படுத்தப்படுகிறது. இரண்டு கட்டங்களும் வேத ஜோதிடத்தில் சமமாக முக்கியமானவை."}},
            {"@type": "Question", "name": "செவ்வாய் தோஷம் என்றால் என்ன?", "acceptedAnswer": {"@type": "Answer", "text": "செவ்வாய் தோஷம் (குஜ தோஷம்) செவ்வாய் ஜாதக கட்டத்தில் 1, 2, 4, 7, 8 அல்லது 12வது வீட்டில் அமரும்போது ஏற்படுகிறது. இது திருமண ஒற்றுமையை பாதிக்கும் என்று நம்பப்படுகிறது. சுமார் 50% மக்களுக்கு ஏதாவது ஒரு வகையான செவ்வாய் தோஷம் உள்ளது. இரு துணைகளுக்கும் செவ்வாய் தோஷம் இருந்தால், அதன் விளைவுகள் நடுநிலையாகும் என்று நம்பப்படுகிறது."}},
            {"@type": "Question", "name": "நட்சத்திரம் என்றால் என்ன? ஏன் முக்கியம்?", "acceptedAnswer": {"@type": "Answer", "text": "நட்சத்திரங்கள் வேத ஜோதிடத்தில் பயன்படுத்தப்படும் 27 சந்திர மாளிகைகள் ஆகும், ஒவ்வொன்றும் ராசி வட்டத்தின் 13°20' பரப்பளவைக் கொண்டவை. உங்கள் பிறப்பு நட்சத்திரம் உங்கள் விம்சோத்தரி திசை வரிசை, ஆளுமை பண்புகள், மற்றும் பிற நட்சத்திரங்களுடன் பொருத்தத்தை தீர்மானிக்கிறது."}}
          ]
        }]}
      />
      <div className="container">
        <motion.div
          className="page-header"
          initial={hydrated ? { opacity: 0, y: -20 } : false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1>இலவச <span className="gradient-text">ஜாதகம்</span> தமிழில்</h1>
          <p>உங்கள் பிறந்த தேதி, நேரம் மற்றும் இடத்தின் அடிப்படையில் விரிவான ஜாதகத்தை உருவாக்குங்கள்</p>
        </motion.div>

        <div className="kundli-content">
          <motion.div
            className="kundli-form-section"
            initial={hydrated ? { opacity: 0, x: -30 } : false}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="form-card">
              <h3>பிறப்பு விவரங்களை உள்ளிடவும்</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>
                    <FiUser className="input-icon" /> முழுப் பெயர்
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="உங்கள் முழுப் பெயரை உள்ளிடவும்"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>பாலினம்</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">பாலினத்தைத் தேர்வு செய்யவும்</option>
                    <option value="male">ஆண்</option>
                    <option value="female">பெண்</option>
                    <option value="other">மற்றவை</option>
                  </select>
                </div>

                <DateTimePicker
                  date={formData.date}
                  time={formData.time}
                  onDateChange={(val) => setFormData(prev => ({ ...prev, date: val }))}
                  onTimeChange={(val) => setFormData(prev => ({ ...prev, time: val }))}
                />

                <div className="form-group form-lang-selector">
                  <label>மொழி</label>
                  <select
                    value="ta"
                    onChange={(e) => {
                      if (e.target.value === 'en') {
                        navigate('/online-horoscope-generator', { state: { formData, selectedLocation } });
                      }
                    }}
                  >
                    <option value="en">English</option>
                    <option value="ta">தமிழ் (Tamil)</option>
                  </select>
                </div>

                <div className="form-group city-search-group" ref={suggestionsRef}>
                  <label>
                    <FiMapPin className="input-icon" /> பிறந்த இடம்
                  </label>
                  <input
                    type="text"
                    name="place"
                    value={formData.place}
                    onChange={handleChange}
                    placeholder="நகரத்தைத் தேடுங்கள்..."
                    required
                    autoComplete="off"
                  />
                  {showSuggestions && suggestions.length > 0 && (
                    <ul className="city-suggestions">
                      {suggestions.map((city, i) => (
                        <li key={i} onClick={() => handleCitySelect(city)}>
                          {city.city}{city.state ? `, ${city.state}` : ''}, {city.country}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'உருவாக்குகிறது...' : 'ஜாதகம் உருவாக்கு'}
                </button>

                {error && <div className="form-error">{error}</div>}
              </form>
            </div>
          </motion.div>

          {horoscope && (
            <motion.div
              ref={resultRef}
              className="kundli-result-section"
              initial={hydrated ? { opacity: 0, x: 30 } : false}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="chart-card">
                <div className="chart-header">
                  <div>
                    <h3>ஜாதக கட்டம் - {horoscope.name}</h3>
                    <p className="birth-info">
                      {horoscope.birthDetails.date} | {horoscope.birthDetails.time} | {horoscope.birthDetails.place}
                    </p>
                  </div>
                </div>

                <BasicDetails horoscope={horoscope} />

                <div className="section-block">
                  <SouthIndianChart
                    rasiChart={horoscope.rasiChart}
                    lagnaRashi={horoscope.lagna.rashiIndex}
                    title="ராசி கட்டம்"
                  />
                  <PlanetTable planets={horoscope.planets} />
                </div>

                <div className="section-block">
                  <SouthIndianChart
                    rasiChart={horoscope.navamsaChart}
                    lagnaRashi={horoscope.navamsaLagnaRashi}
                    title="நவாம்சம் (D-9)"
                  />
                  {horoscope.divisionalCharts?.navamsa && (
                    <div className="planets-table">
                      <h4>நவாம்ச கிரக நிலைகள்</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>கிரகம்</th>
                            <th>நவாம்ச ராசி</th>
                          </tr>
                        </thead>
                        <tbody>
                          {horoscope.divisionalCharts.navamsa.map((p, i) => (
                            <tr key={i}>
                              <td>{PLANET_TAMIL[p.planet] || p.planet}</td>
                              <td>{toTamil(p.rashi, RASHI_TAMIL)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                <div className="section-block">
                  <DashaSection dasha={horoscope.dasha} />
                </div>

                <div className="section-block">
                  <ShadbalaSection
                    shadbala={horoscope.shadbala}
                    manglik={horoscope.manglik}
                  />
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom SEO Content - Tamil */}
        <div className="page-bottom-content">
          <h2>இலவச ஜாதக கட்டம் (ஜாதகம்) உருவாக்கி - தமிழில்</h2>
          <p>AstroEpanchang இன் இலவச ஜாதக உருவாக்கி உங்கள் பிறந்த தேதி, நேரம் மற்றும் இடத்தின் அடிப்படையில் விரிவான வேத ஜாதகத்தை (ஜாதக கட்டம்) தமிழில் உருவாக்குகிறது. எங்கள் கணக்கீடுகள் லஹிரி அயனாம்சம் மற்றும் உண்மையான வேத ஜோதிட கொள்கைகளைப் பயன்படுத்தி துல்லியமான ராசி கட்டம், நவாம்ச (D-9) கட்டம், நட்சத்திரம் மற்றும் பாத விவரங்களுடன் கிரக நிலைகளை வழங்குகின்றன.</p>
          <p>ஜாதக கட்டம் உங்கள் லக்னம் (ஆரோகணம்), ராசி (சந்திர ராசி), பிறந்த நட்சத்திரம், மற்றும் 12 வீடுகளில் அனைத்து ஒன்பது கிரகங்களின் (நவக்கிரகம்) நிலைகளை வெளிப்படுத்துகிறது. எங்கள் கருவி 120 வருட விம்சோத்தரி திசை பகுப்பாய்வு, மகா திசை மற்றும் அந்தர் திசை (புக்தி) காலங்கள், ஷட்பல கிரக வலிமை கணக்கீடுகள் மற்றும் செவ்வாய் தோஷ மதிப்பீடு ஆகியவற்றை உள்ளடக்கியது.</p>

          <h3>ஜாதகம் (ஜாதக கட்டம்) என்றால் என்ன?</h3>
          <p>ஜாதகம், ஜனன பத்திரிகை அல்லது ஜாதக குண்டலி என்றும் அழைக்கப்படுகிறது. இது ஒரு நபரின் பிறந்த தேதி, நேரம் மற்றும் இடத்தின் அடிப்படையில் தயாரிக்கப்படும் விரிவான ஜோதிட வரைபடம் ஆகும். வேத ஜோதிடத்தில், ஜாதகம் பிறப்பின் தருணத்தில் சூரியன், சந்திரன் மற்றும் அனைத்து கிரகங்களின் துல்லியமான நிலைகளை 12 வீடுகள் (பாவங்கள்) மற்றும் 12 ராசிகளில் வரைபடமாக்குகிறது. இந்த பிரபஞ்ச ஸ்னாப்ஷாட் ஒரு நபரின் குணாதிசயம், விதி, தொழில், உறவுகள் மற்றும் வாழ்க்கை நிகழ்வுகளை புரிந்து கொள்ள ஜோதிடர்களுக்கு உதவுகிறது.</p>

          <h3>உங்கள் ஜாதகத்தை எப்படி படிப்பது?</h3>
          <p>ஜாதகத்தை படிப்பதில் பல முக்கிய கூறுகளை பகுப்பாய்வு செய்வது அடங்கும். லக்னம் (ஆரோகணம்) — பிறப்பின் போது கிழக்கு அடிவானத்தில் உதிக்கும் ராசி — அனைத்து 12 வீடுகளின் அமைப்பை தீர்மானிக்கிறது. சந்திர ராசி உங்கள் உணர்ச்சி இயல்பை குறிக்கிறது மற்றும் தினசரி கணிப்புகளுக்கு பயன்படுத்தப்படுகிறது. முதல் வீடு சுயம் மற்றும் ஆளுமையை குறிக்கிறது, 7வது வீடு திருமணத்தை ஆளுகிறது, 10வது வீடு தொழில் மற்றும் வேலையை உள்ளடக்கியது, 4வது வீடு வீடு மற்றும் தாயுடன் தொடர்புடையது.</p>

          <h3>விம்சோத்தரி திசை — வேத ஜோதிடத்தில் கிரக காலங்கள்</h3>
          <p>விம்சோத்தரி திசை வேத ஜோதிடத்தில் ஒரு தனித்துவமான 120 வருட நேர அமைப்பாகும், இது ஒன்பது கிரகங்களிடையே பிரிக்கப்படுகிறது. ஒவ்வொரு மகா திசையும் (பெரிய காலம்) ஒரு கிரகத்தால் ஆளப்படுகிறது: சூரியன் (6 வருடங்கள்), சந்திரன் (10 வருடங்கள்), செவ்வாய் (7 வருடங்கள்), ராகு (18 வருடங்கள்), குரு (16 வருடங்கள்), சனி (19 வருடங்கள்), புதன் (17 வருடங்கள்), கேது (7 வருடங்கள்), சுக்கிரன் (20 வருடங்கள்). வரிசை உங்கள் பிறப்பு நட்சத்திரத்தை ஆளும் கிரகத்துடன் தொடங்குகிறது.</p>

          <h3>அடிக்கடி கேட்கப்படும் கேள்விகள்</h3>
          <div className="faq-seo">
            <div className="faq-seo-item">
              <h4>இலவச ஜாதக உருவாக்கி துல்லியமானதா?</h4>
              <p>ஆம், AstroEpanchang சுவிஸ் எபிமெரிஸ் கணக்கீடுகளை லஹிரி அயனாம்சத்துடன் பயன்படுத்துகிறது — இது இந்தியாவில் தொழில்முறை ஜோதிடர்கள் பயன்படுத்தும் அதே தரநிலையாகும். துல்லியம் சரியான பிறப்பு நேரத்தை உள்ளிடுவதில் தங்கியுள்ளது, ஏனெனில் சில நிமிட வேறுபாடு கூட லக்னம் மற்றும் திசை காலத்தை மாற்றக்கூடும்.</p>
            </div>
            <div className="faq-seo-item">
              <h4>ஜாதகம் உருவாக்க என்ன தகவல்கள் தேவை?</h4>
              <p>உங்கள் சரியான பிறந்த தேதி, பிறந்த நேரம் (முடிந்தவரை துல்லியமாக — மருத்துவமனை பதிவுகள் சிறந்தவை), மற்றும் பிறந்த இடம் (நகரம்/ஊர்) தேவை. பிறந்த இடம் புவியியல் ஆயத்தொலைவுகள் மற்றும் நேர வலயத்தை தீர்மானிக்க பயன்படுத்தப்படுகிறது.</p>
            </div>
            <div className="faq-seo-item">
              <h4>ராசி கட்டம் மற்றும் நவாம்ச கட்டம் என்ன வேறுபாடு?</h4>
              <p>ராசி கட்டம் (D-1) பிறப்பின் போது கிரக நிலைகளைக் காட்டும் முதன்மை ஜாதக கட்டமாகும். நவாம்ச கட்டம் (D-9) ஒவ்வொரு ராசியையும் 9 சம பாகங்களாக பிரிக்கிறது மற்றும் திருமண பொருத்தம் மற்றும் ஆன்மீக பகுப்பாய்வுக்கு பயன்படுத்தப்படுகிறது. இரண்டு கட்டங்களும் வேத ஜோதிடத்தில் சமமாக முக்கியமானவை.</p>
            </div>
            <div className="faq-seo-item">
              <h4>செவ்வாய் தோஷம் என்றால் என்ன?</h4>
              <p>செவ்வாய் தோஷம் (குஜ தோஷம்) செவ்வாய் ஜாதக கட்டத்தில் 1, 2, 4, 7, 8 அல்லது 12வது வீட்டில் அமரும்போது ஏற்படுகிறது. இது திருமண ஒற்றுமையை பாதிக்கும் என்று நம்பப்படுகிறது. சுமார் 50% மக்களுக்கு ஏதாவது ஒரு வகையான செவ்வாய் தோஷம் உள்ளது. இரு துணைகளுக்கும் செவ்வாய் தோஷம் இருந்தால், அதன் விளைவுகள் நடுநிலையாகும் என்று நம்பப்படுகிறது.</p>
            </div>
            <div className="faq-seo-item">
              <h4>நட்சத்திரம் என்றால் என்ன? ஏன் முக்கியம்?</h4>
              <p>நட்சத்திரங்கள் வேத ஜோதிடத்தில் பயன்படுத்தப்படும் 27 சந்திர மாளிகைகள் ஆகும், ஒவ்வொன்றும் ராசி வட்டத்தின் 13°20' பரப்பளவைக் கொண்டவை. உங்கள் பிறப்பு நட்சத்திரம் (பிறப்பின் போது சந்திரன் அமர்ந்த நட்சத்திரம்) உங்கள் விம்சோத்தரி திசை வரிசை, ஆளுமை பண்புகள், மற்றும் பிற நட்சத்திரங்களுடன் பொருத்தத்தை தீர்மானிக்கிறது. இது திருமண பொருத்தம், நாமகரணம் மற்றும் ஆன்மீக நடைமுறைகளுக்கு மிக அடிப்படையான உறுப்பாகும்.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JathagamInTamil;
